import { useEffect, useMemo, useState } from "react";

import {
  Menu,
  MenuButton,
  MenuList,
  MenuDivider,
  IconButton,
  Table as ChakraTable,
  Thead,
  Tbody,
  Tr,
  Td,
  Th,
  Flex,
  Box,
  Button,
  Text,
  Input,
  MenuItemOption,
  MenuOptionGroup,
  Switch,
  useColorModeValue
} from '@chakra-ui/react'

import {
  Column,
  Table as TanTable,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  sortingFns,
  getSortedRowModel,
  FilterFn,
  SortingFn,
  ColumnDef,
  flexRender,
} from "@tanstack/react-table";

import {
  RankingInfo,
  rankItem,
  compareItems,
} from "@tanstack/match-sorter-utils";
import { BsFilter } from "react-icons/bs";
import { THeader } from "./table-styles";
import { ArrowDownIcon, ArrowUpIcon } from "@chakra-ui/icons";
import { MdChevronLeft, MdChevronRight, MdSettings, MdSkipNext, MdSkipPrevious } from "react-icons/md";
import { pagination as paginationSizes } from "@bemap/utils";
import { useTranslation } from "react-i18next";
import { DebouncedInput } from "../form-fields";

declare module "@tanstack/table-core" {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);

  addMeta({
    itemRank,
  });

  return itemRank.passed;
};

export const fuzzySort: SortingFn<any> = (rowA, rowB, columnId) => {
  let dir = 0;

  // Only sort by rank if the column has ranking information
  if (rowA.columnFiltersMeta[columnId]) {
    dir = compareItems(
      rowA.columnFiltersMeta[columnId]?.itemRank!,
      rowB.columnFiltersMeta[columnId]?.itemRank!
    );
  }

  // Provide an alphanumeric fallback for when the item ranks are equal
  return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir;
};

const saveStateToSessionStorage = (tableId: string, key: string, value: any) => {
  const tableState = JSON.parse(sessionStorage.getItem(tableId) || '{}');
  tableState[key] = value;
  sessionStorage.setItem(tableId, JSON.stringify(tableState));
};

const getStateFromSessionStorage = (tableId: string, key: string) => {
  const tableState = JSON.parse(sessionStorage.getItem(tableId) || '{}');
  return tableState[key] || null;
};

export const Table = ({
  data,
  columns,
  printing,
  showGeralSearch,
  tableId = 'default_table',
  onTableReady
}: {
  data: any[],
  columns: ColumnDef<any, any>[]
  printing?: boolean
  showGeralSearch?: boolean
  tableId?: string
  onTableReady?: (table: TanTable<any>) => void
}) => {

  const { t } = useTranslation();

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    getStateFromSessionStorage(tableId, 'columnFilters') || []
  );
  const [globalFilter, setGlobalFilter] = useState(
    getStateFromSessionStorage(tableId, 'globalFilter') || ""
  );
  const [columnVisibility, setColumnVisibility] = useState(
    getStateFromSessionStorage(tableId, 'columnVisibility') || {}
  );
  const [sorting, setSorting] = useState(
    getStateFromSessionStorage(tableId, 'sorting') || []
  );
  const [pagination, setPagination] = useState(() => {
    const savedPagination = getStateFromSessionStorage(tableId, 'pagination');
    return savedPagination || { pageIndex: 0, pageSize: paginationSizes.pages[0] };
  });

  const table = useReactTable({
    data,
    columns,
    initialState: {
      pagination,
    },
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      columnVisibility,
      sorting,
      pagination,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  useEffect(() => {
    saveStateToSessionStorage(tableId, 'columnFilters', columnFilters);
    saveStateToSessionStorage(tableId, 'globalFilter', globalFilter);
    saveStateToSessionStorage(tableId, 'columnVisibility', columnVisibility);
    saveStateToSessionStorage(tableId, 'sorting', sorting);
    saveStateToSessionStorage(tableId, 'pagination', pagination);
  }, [tableId, columnFilters, globalFilter, columnVisibility, sorting, pagination]);

  useEffect(() => {
    if (onTableReady) {
      onTableReady(table);
    }
  }, [table, onTableReady]);

  useEffect(() => {
    if (printing) {
      const oldPagination = { ...pagination };
      table.setPageSize(table.getPrePaginationRowModel().rows.length);
      return () => {
        table.setPagination(oldPagination);
      };
    }
  }, [printing, table]);

  return (
    <Flex direction={'column'} overflowX={'auto'}>
      {showGeralSearch && (
        <>
          <Flex px='13px' alignItems={'center'} gap='15px' className="noprint">
            <DebouncedInput
              value={globalFilter ?? ""}
              onChange={(value: string | number) => setGlobalFilter(String(value))}
              className="p-2 font-lg shadow border border-block"
              placeholder={t("table.search_placeholder")}
            />
            <Menu closeOnSelect={false}>
              {({ isOpen }) => (
                <>
                  <MenuButton background={'secondaryGray.300'} variant={'solid'} isActive={isOpen} isRound={true} as={IconButton} icon={<MdSettings />} />
                  <MenuList minWidth='240px'>
                    <MenuOptionGroup title='Colunas' type='checkbox'>
                      {table.getAllLeafColumns().map(column => {
                        return (
                          <MenuItemOption key={column.id} value={column.id} as={Switch} colorScheme='brand' defaultChecked={column.getIsVisible()} onChange={column.getToggleVisibilityHandler()}>
                            {column.id}
                          </MenuItemOption>
                        )
                      })}
                    </MenuOptionGroup>
                  </MenuList>
                </>
              )}
            </Menu>
          </Flex>
          <div className="h-2" />
        </>

      )}
      <Box overflowX={'auto'}>
        <ChakraTable size='sm'>
          <Thead bg={useColorModeValue("gray.100", "gray.800")}>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <Th cursor={'pointer'} key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder ? null : (
                        <THeader>
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? "cursor-pointer select-none"
                                : "",
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </div>
                          <div>
                            {{
                              asc: <ArrowUpIcon />,
                              desc: <ArrowDownIcon />,
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                          {header.column.getCanFilter() ? (
                            <Menu closeOnBlur={true} isLazy={true} placement="bottom-end">
                              {({ isOpen }) => (
                                <>
                                  <Box position="relative">
                                    <MenuButton
                                      isActive={isOpen}
                                      as={IconButton}
                                      variant='ghost'
                                      aria-label="filter"
                                      size='sm'
                                      icon={<BsFilter />}
                                    />
                                    {header.column.getFilterValue() ? (
                                      <Box
                                        position="absolute"
                                        top="-1px"
                                        right="-1px"
                                        width="8px"
                                        height="8px"
                                        borderRadius="full"
                                        bg="blue.500"
                                      />
                                    ) : null}
                                  </Box>
                                  <MenuList maxH={'350px'}>
                                    <Filter column={header.column} table={table} />
                                  </MenuList>
                                </>
                              )}

                            </Menu>
                          ) : null}
                        </THeader>
                      )}
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <Tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <Td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </ChakraTable>
      </Box>
      <Flex my='20px' justifyContent={'space-between'} flexDirection={{ base: 'column', lg: 'row' }} alignItems={'center'} className="noprint">
        <Flex alignItems={'center'} gap={{ base: '10px', lg: '30px' }} pl='15px'>
          <strong>{table.getPrePaginationRowModel().rows.length} {t('table.records')}</strong>
        </Flex>
        <Flex flexDirection={{ base: 'column', lg: 'row' }} justifyContent={'flex-end'} alignItems={'center'} gap={{ base: '20px', lg: '30px' }} >
          {table.getPrePaginationRowModel().rows.length > 0 && (
            <>
              <Flex gap='5px'>
                <Text>{t('table.page')}</Text>
                <Text>
                  {table.getState().pagination.pageIndex + 1} {t('table.of')} {table.getPageCount()}
                </Text>
              </Flex>
              <Flex gap='10px' display={{ base: 'none', lg: 'flex' }}>
                <Text>{t('table.goTo')}</Text>
                <Input
                  width={'30px'}
                  variant='unstyled'
                  type="number"
                  value={table.getState().pagination.pageIndex + 1}
                  onChange={(e) => {
                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                    table.setPageIndex(page);
                  }}
                />
              </Flex>
              <Flex gap='10px'>
                <Text>{t('table.lines')}</Text>
                <select
                  aria-label={t('table.lines')}
                  value={table.getState().pagination.pageSize}
                  onChange={(e) => {
                    table.setPageSize(Number(e.target.value));
                  }}
                >
                  {paginationSizes.pages.map((pageSize) => (
                    <option key={pageSize} value={pageSize}>
                      {pageSize}
                    </option>
                  ))}
                </select>
              </Flex>
            </>
          )}
          <Flex alignItems={'center'} justifyContent={{ base: 'space-between', lg: 'center' }} w={{ base: '100%', lg: 'unset' }} gap={'10px'} mr={4}>
            <IconButton
              variant='ghost'
              isRound={true}
              aria-label={t('table.first_page')}
              onClick={() => table.setPageIndex(0)}
              isDisabled={!table.getCanPreviousPage()}
              color={useColorModeValue('gray.700', 'white')}
              icon={<MdSkipPrevious size='18px' />}
              size='sm'
            />
            <IconButton
              variant='ghost'
              isRound={true}
              aria-label={t('table.previous_page')}
              onClick={() => table.previousPage()}
              isDisabled={!table.getCanPreviousPage()}
              color={useColorModeValue('gray.700', 'white')}
              icon={<MdChevronLeft size='18px' />}
              size='sm'
            />
            <IconButton
              variant='ghost'
              isRound={true}
              aria-label={t('table.next_page')}
              onClick={() => table.nextPage()}
              isDisabled={!table.getCanNextPage()}
              color={useColorModeValue('gray.700', 'white')}
              icon={<MdChevronRight size='18px' />}
              size='sm'
            />
            <IconButton
              variant='ghost'
              isRound={true}
              aria-label={t('table.last_page')}
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              isDisabled={!table.getCanNextPage()}
              color={useColorModeValue('gray.700', 'white')}
              icon={<MdSkipNext size='18px' />}
              size='sm'
            />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}

function Filter({
  column,
  table,
}: {
  column: Column<any, unknown>;
  table: TanTable<any>;
}) {
  const { t } = useTranslation();

  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id);

  const columnFilterValue = column.getFilterValue();

  const sortedUniqueValues = useMemo(
    () =>
      typeof firstValue === "number"
        ? []
        : Array.from(column.getFacetedUniqueValues().keys()).sort(),
    [column.getFacetedUniqueValues()]
  );

  return typeof firstValue === "number" ? (
    <Flex direction='column'>
      <Box px='15px' py='5px'>
        <b>{t('table.filter')}</b>
      </Box>
      <MenuDivider />
      <Box px='15px'>
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[0] ?? ""}
          onChange={(value: string | number) =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min ${column.getFacetedMinMaxValues()?.[0]
            ? `(${column.getFacetedMinMaxValues()?.[0]})`
            : ""
            }`}
          className="w-24 border shadow rounded"
        />
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[1] ?? ""}
          onChange={(value: string | number) =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Máx ${column.getFacetedMinMaxValues()?.[1]
            ? `(${column.getFacetedMinMaxValues()?.[1]})`
            : ""
            }`}
          className="w-24 border shadow rounded"
        />
      </Box>
      <MenuDivider />
      <Flex gap='15px' px='10px'>
        <Button size='sm' variant='outline' onClick={() => column.setFilterValue('')}>{t('table.search_reset')}</Button>
      </Flex>
    </Flex>
  ) : (
    <Flex direction='column'>
      <Box px='15px' py='5px'>
        <b>{t('table.filter')}</b>
      </Box>
      <MenuDivider />
      <Box px='15px'>
        <DebouncedInput
          type="text"
          value={(columnFilterValue ?? "") as string}
          onChange={(value: string | number) => column.setFilterValue(value)}
          placeholder={`${t('table.search_placeholder')} (${column.getFacetedUniqueValues().size})`}
          className="w-36 border shadow rounded"
          list={column.id + "_list"}
        />
        <datalist id={column.id + "_list"}>
          {sortedUniqueValues.slice(0, 5000).map((value: any) => (
            <option value={value} key={value} />
          ))}
        </datalist>
      </Box>
      <MenuDivider />
      <Flex gap='15px' px='10px'>
        <Button size='sm' variant='outline' onClick={() => column.setFilterValue('')}>{t('table.search_reset')}</Button>
      </Flex>
    </Flex>
  );
}

export * from './row-actions'